import { Logger } from './logger'
import { DEFAULT_REQUESTOR, DEFAULT_TENANT, NATIONAL_PEN_TENANT } from '../constants/config'

const URL_SERVICE_BASE = 'https://url.prod.merch.vpsvc.com/v3'
const AVAILABILITY_SERVICE_BASE = 'https://page-availability-service.prod.merch.vpsvc.com/api/v2'
const DISCOVERY_SERVICE_BASE = 'https://discovery-pages.large-assortment.vpsvc.com/v1/discoverypageequivalents'

/**
 * Returns a relative URL (pathname + search + hash) for National Pen, which is served
 * from a single domain and must not navigate to an external origin.
 * Returns the original absolute URL for all other tenants.
 */
export function resolveUrlForTenant(url: string, tenant?: string): string {
  if (tenant !== NATIONAL_PEN_TENANT) return url
  try {
    const { pathname, search, hash } = new URL(url)
    return `${pathname}${search}${hash}`
  } catch {
    return url
  }
}

/**
 * Map of URL service route types to page availability route types.
 * @see https://url.prod.merch.vpsvc.com/v3/types/vistaprint?requestor=bookends
 */
const ROUTE_TYPE: Record<string, string> = {
  Category: 'CategoryPage',
  Product: 'ProductPage',
  Other: 'HomePage',
  Static: 'StaticPage',
  SpecialOffers: 'SpecialOffersPage',
  Offer: 'SpecialOffersPage',
  MarketingLanding: 'MarketingLandingPage',
  '': 'HomePage',
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`)
  }
  return response.json() as Promise<T>
}

/** Resolves the URL service ID for a given path and locale */
async function resolveUrlId(path: string, locale: string, tenant: string): Promise<{ id: string; type: string; url: string }> {
  const encoded = encodeURIComponent(path)
  const data = await fetchJson<{ id: string; type: string; url: string }>(
    `${URL_SERVICE_BASE}/id/${tenant}/${locale}/${encoded}?requestor=${DEFAULT_REQUESTOR}`,
  )
  return data
}

/** Resolves the URL for a given ID and target locale */
async function resolveUrlForLocale(id: string, targetLocale: string, tenant: string): Promise<{ id: string; type: string; url: string }> {
  return fetchJson<{ id: string; type: string; url: string }>(`${URL_SERVICE_BASE}/url/${tenant}/${targetLocale}/${id}?requestor=${DEFAULT_REQUESTOR}`)
}

/** Checks if a route is available in the target locale via the availability service */
async function checkRouteAvailability(id: string, routeType: string, targetLocale: string, tenant: string): Promise<void> {
  const routes = await fetchJson<Array<{ urlId: string }>>(`${AVAILABILITY_SERVICE_BASE}/${tenant}/${routeType}/url/${targetLocale}`)
  const found = routes.find((route) => route.urlId === id)
  if (!found) {
    throw new Error('URL is not active')
  }
}

/** Checks if a discovery page equivalent exists for the target locale */
async function checkDiscoveryPage(discoveryId: string, targetLocale: string, tenant: string): Promise<boolean> {
  const data = await fetchJson<Record<string, unknown>>(`${DISCOVERY_SERVICE_BASE}/${tenant}/${discoveryId}?requestor=${DEFAULT_REQUESTOR}`)
  if (data?.[targetLocale]) return true
  throw new Error('URL is not active')
}

/**
 * Determines the redirect URL when a user switches locale.
 * Looks up the equivalent page in the target locale using the URL service
 * and validates its availability.
 *
 * @returns The target URL path, or '/' if the page doesn't exist in the target locale.
 */
export async function getRedirectURL({
  currentUrl,
  requestLocale,
  locale,
  tenant = DEFAULT_TENANT,
}: {
  currentUrl: string
  requestLocale: string
  locale: string
  tenant?: string
}): Promise<string> {
  try {
    // Preserve search params for the search page
    if (currentUrl === '/search' && globalThis.window) {
      return `/search${globalThis.location.search}`
    }

    const data = await resolveUrlId(currentUrl, locale, tenant)
    const targetUrl = await resolveUrlForLocale(data.id, requestLocale, tenant)

    let type = targetUrl.type
    if (data.id === 'other:offers') {
      type = 'Offer'
    }

    const routeType = ROUTE_TYPE[type]

    if (routeType === 'DiscoveryPage') {
      await checkDiscoveryPage(data.id, requestLocale.toLowerCase(), tenant)
    } else if (routeType) {
      await checkRouteAvailability(data.id, routeType, requestLocale.toLowerCase(), tenant)
      return targetUrl.url
    }

    return '/'
  } catch (error) {
    Logger.instance.info('Page availability redirect fallback', { currentUrl, requestLocale, error })
    return '/'
  }
}
